import { HttpStatusCode } from 'axios';

import type {
  ResolvableCitationEntity,
  ResolvedCitationEntity,
} from '~/features/ask-anything/types/chat';

import { intlService } from '~/i18n/IntlService';
import { fetchEverest } from '~/redux/api/everest/baseQuery';
import { getTicketDocument } from '~/redux/api/k2/methods/getTicketDocument';
import { RoutePathType } from '~/routing/enums';
import { TicketDocument } from '~/types/Ticket';

import { testIsAxiosError } from '../../utils';
import { getDocumentHandler } from '../document/getDocumentHandler';

export const resolveCitation = async (
  entity: ResolvableCitationEntity,
): Promise<ResolvedCitationEntity | undefined> => {
  if (!entity) return undefined;
  switch (entity.type) {
    case 'ticket': {
      const result = await fetchEverest({
        url: `tickets/${entity.id}/document`,
        rtkQueryOptions: { showErrorToast: false },
      });
      if (result.error) {
        throw result.error;
      }
      const ticketDocument = result.data as TicketDocument;

      return {
        //l10n-ignore: API parsing no formatting required
        lastModified: new Date(ticketDocument.modifiedDate),
        fileName: `${ticketDocument.name}${ticketDocument.fileType}`,
        link: RoutePathType.WorkflowTicketsTicketReviewerVersionNumber.replace(
          ':ticketId',
          entity.id,
        ).replace(':versionNumber', `${ticketDocument.version}`),
      };
    }
    case 'ticket_v2_document': {
      const { ticketId, documentId, version } = entity;
      const ticketV2Document = await getTicketDocument({
        ticketId,
        documentId,
        version,
      });

      return {
        lastModified: ticketV2Document.lastModified,
        fileName: ticketV2Document.fileType
          ? `${ticketV2Document.fileName}.${ticketV2Document.fileType}`
          : ticketV2Document.fileName,
        link: RoutePathType.WorkflowCLMEvolutionRequestsDetailDocument.replace(
          ':ticketId',
          ticketId,
        )
          .replace(':documentId', documentId)
          .replace(':version', `${version}`),
      };
    }
    case 'document_handler':
      try {
        const documentHandler = await getDocumentHandler(entity.id);
        return {
          lastModified: documentHandler.dateModified,
          link: RoutePathType.Document.replace(':documentId', entity.id),
          fileName: `${documentHandler.documentName}${documentHandler.fileType}`,
          path: documentHandler.path.map((path) => path.name).join('/'),
        };
      } catch (error: unknown) {
        if (
          testIsAxiosError(error) &&
          error.response?.status === HttpStatusCode.NotFound
        ) {
          throw new Error(
            intlService.getIntl().formatMessage({
              id: 'resolveCitation.fileDeleted',
              defaultMessage: 'This file has been deleted.',
              description: 'Error message when cited file has been deleted',
            }),
            {
              cause: HttpStatusCode.NotFound,
            },
          );
        }
      }
  }
};
